import clsx from "clsx";
import { ReactNode, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMatch, useSearchParams } from "react-router-dom";

import { Filter, Search } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";

import { FilterDialog } from "./FilterDialog/FilterDialog";
import { SearchBar } from "./SearchBar/SearchBar";
import { FilterDialogForm } from "./types";
import {
  FILTER_PARAM_KEYS,
  getFilterAndSearchParams,
  getFormStateFromSearchParams,
} from "./utils";

interface PortalDeviceListFilterProps {
  /** The route used for the device list view */
  listRoute: string;
  /**
   * Tiles/table switch, rendered at the trailing end of the same toolbar row
   * as the search and filter buttons.
   */
  viewToggle: ReactNode;
}

/**
 * Main component for filtering the portal's device list.
 * Includes a search bar and a detailed filter dialog.
 */
export const PortalDeviceListFilter = ({
  listRoute,
  viewToggle,
}: PortalDeviceListFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const isListView = useMatch(listRoute);
  const { t } = useTranslation("portal");

  const { watch, control, setValue, register, reset } =
    useForm<FilterDialogForm>({
      defaultValues: getFormStateFromSearchParams(searchParams),
    });

  const formState = watch();

  /**
   * Removes all filter-related URL parameters.
   *
   * @param searchParams - The current URLSearchParams.
   * @returns The updated query string.
   */
  const removeURLParams = (searchParams: URLSearchParams) => {
    const updatedSearchParams = new URLSearchParams(searchParams.toString());
    FILTER_PARAM_KEYS.forEach((key) => updatedSearchParams.delete(key));

    return updatedSearchParams.toString();
  };

  /**
   * Closes the filter modal and updates the URL search parameters based on the current form state.
   */
  const closeModal = () => {
    const updatedSearchParams = getFilterAndSearchParams(
      formState,
      searchParams,
    ).toString();
    setSearchParams(updatedSearchParams);
    setIsOpen(false);
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const resetFilters = () => {
    const resettedSearchParams = removeURLParams(searchParams);
    reset();
    setSearchParams(resettedSearchParams);
    setIsOpen(false);
  };

  const hasFormFields =
    formState.simVoiceOption !== "ALL" ||
    formState.simAutoRenew !== "ALL" ||
    formState.simStatus.length > 0 ||
    formState.simType.length > 0 ||
    formState.additionalColumns.length > 0;

  // The applied state is whatever is currently persisted in the URL.
  const appliedFormState = getFormStateFromSearchParams(searchParams);

  const hasAppliedFilters =
    appliedFormState.simVoiceOption !== "ALL" ||
    appliedFormState.simAutoRenew !== "ALL" ||
    appliedFormState.simStatus.length > 0 ||
    appliedFormState.simType.length > 0 ||
    appliedFormState.additionalColumns.length > 0;

  const searchInputRef = useRef<HTMLInputElement>(null);

  /**
   * Focuses the search input field.
   * To make it more user-friendly, we want to enable typing once the search-field is opened.
   */
  const focusSearchInput = () => {
    searchInputRef.current?.focus();
  };

  const blurSearchInput = () => {
    searchInputRef.current?.blur();
  };

  const toggleSearch = () => {
    setSearchIsOpen((prev) => {
      const newState = !prev;
      if (newState) {
        // Needed to fix a race-condition when closing the search bar via button click
        setTimeout(() => focusSearchInput(), 0);
      } else {
        blurSearchInput();
      }

      return newState;
    });
  };

  const handleSearchToggleMouseDown = (e: React.MouseEvent) => {
    // Verhindert, dass der Toggle-Button den Fokus stiehlt, was ein onBlur auf dem Input auslösen würde.
    // Dies verhindert einen Race-Condition-Effekt, bei dem onBlur die Bar schließt, bevor onClick sie wieder öffnet.
    e.preventDefault();
  };

  return (
    <div className="flex flex-col gap-gutter">
      {/* Mobile shows the search field permanently above the button row; from
          tablet up it stays collapsed behind the search button next to the
          filter, which is why the toggle only exists there. */}
      <SearchBar
        className={clsx(!searchIsOpen && "tablet:hidden")}
        onEmptyBlur={() => setSearchIsOpen(false)}
        inputRef={searchInputRef}
      />
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="hidden tablet:block">
            <Button
              ariaLabel={t("cockpit.filter.searchBar.toggle")}
              variant="tertiary"
              icon={<Search />}
              pressed={searchIsOpen}
              onClick={toggleSearch}
              onMouseDown={handleSearchToggleMouseDown}
            />
          </div>
          <div className="relative">
            <Button
              ariaLabel={t("cockpit.filter.openFilters")}
              variant="tertiary"
              icon={<Filter />}
              pressed={isOpen}
              onClick={openModal}
            />
            {hasFormFields && (
              <span
                data-testid="active-filters-indicator"
                aria-hidden="true"
                className="pointer-events-none absolute right-1 top-1 size-2 rounded-full bg-surface-primary"
              />
            )}
          </div>
        </div>
        {viewToggle}
      </div>
      <FilterDialog
        isOpen={isOpen}
        onClose={closeModal}
        onReset={resetFilters}
        isListView={!!isListView}
        watch={watch}
        setValue={setValue}
        register={register}
        control={control}
        hasFormFields={hasFormFields}
        hasAppliedFilters={hasAppliedFilters}
      />
    </div>
  );
};
